'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useCart as usePluginCart } from '@payloadcms/plugin-ecommerce/client/react'
import { useAuth } from '@/providers/Auth'
import {
  calculateShipping,
  getShippingCountry,
  ShippingCountry,
  resolveItemWeight,
} from '@/utilities/shippingCalculator'
import type { ShippingInfo } from '@/components/checkout/ShippingCalculator'
import { hasManualDeliveryItems } from '@/utilities/manualDeliveryDetector'

// Types
type CouponResponse = {
  success: boolean
  message?: string
}

export interface DiscountBreakdownItem {
  discountName: string
  discountMode: 'manual' | 'bulk' | 'automatic'
  productTitle: string
  productId: number
  originalPrice: number // in cents
  discountAmount: number // in cents
  discountPercentage: number
  quantity: number
}

export interface EnhancedCartContextType {
  // From plugin
  cart: any
  addItem: (item: any, quantity?: number) => void
  removeItem: (id: number) => Promise<void>
  clearCart: () => void
  incrementItem: (id: number) => Promise<void>
  decrementItem: (id: number) => Promise<void>
  isLoading: boolean
  // Enhanced features
  cartSubtotal: number
  cartTotal: {
    formatted: string
    raw: number
  }
  shippingInfo: ShippingInfo | null
  shippingCountry: ShippingCountry
  setShippingCountry: (country: ShippingCountry) => void
  bulkVolumeDiscount: number
  discountBreakdown: DiscountBreakdownItem[]
  manualCouponDiscount: number
  couponId: string | null
  applyCoupon: (promoCode: string) => Promise<CouponResponse>
  removeCoupon: () => void
  hasInitializedCart: boolean
  cartIsEmpty: boolean
  containsManualDeliveryItems: boolean
}

const EnhancedCartContext = createContext<EnhancedCartContextType>({} as EnhancedCartContextType)

export const useEnhancedCart = () => useContext(EnhancedCartContext)

/**
 * Helper to get price from a cart item (handles variant pricing)
 */
function getItemPrice(item: any): number {
  const product = item.product
  if (!product || typeof product !== 'object') return 0

  // Headless variant pricing
  if (item.selectedVariantData && item.selectedVariantData.priceTotal !== undefined) {
    return Math.round(Number(item.selectedVariantData.priceTotal) * 100)
  }

  // Check for variant pricing first
  if (item.variant && typeof item.variant === 'object') {
    const variantPrice = item.variant?.priceInEUR || item.variant?.price
    if (variantPrice !== undefined && variantPrice !== null) {
      // DB stores prices in EUR (e.g. 1402.80), frontend needs cents (140280)
      return Math.round(Number(variantPrice) * 100)
    }
  }

  // Fallback to base price
  const basePrice = product.basePrice || product.price || 0
  // DB stores prices in EUR (e.g. 1402.80), frontend needs cents (140280)
  return Math.round(Number(basePrice) * 100)
}

export const EnhancedCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const pluginCart = usePluginCart()
  const { cart, addItem, removeItem, clearCart, incrementItem, decrementItem, isLoading } =
    pluginCart

  // Enhanced state
  const [shippingCountry, setShippingCountry] = useState<ShippingCountry>('AT')
  const [couponId, setCouponId] = useState<string | null>(null)

  // Store full coupon details for dynamic calculation
  const [couponDetails, setCouponDetails] = useState<any | null>(null)

  const [bulkVolumeDiscount, setBulkVolumeDiscount] = useState(0)
  const [discountBreakdown, setDiscountBreakdown] = useState<DiscountBreakdownItem[]>([])
  const [hasInitializedCart, setHasInitializedCart] = useState(false)

  // Mark cart as initialized when plugin cart is no longer loading
  useEffect(() => {
    if (!isLoading && cart) {
      setHasInitializedCart(true)
    }
  }, [isLoading, cart])

  // Determine if cart contains manual delivery items
  const containsManualDeliveryItems = useMemo(() => {
    if (!cart?.items || !Array.isArray(cart.items)) return false
    return hasManualDeliveryItems(cart.items as any[])
  }, [cart])

  // Calculate cart subtotal in cents
  // ALWAYS calculate manually from items to ensure all products are included
  // (The plugin's cart.subtotal may exclude certain product types like manual delivery items)
  const cartSubtotal = useMemo((): number => {
    if (!cart?.items || !Array.isArray(cart.items)) return 0

    const calculatedSubtotal = (cart.items as any[]).reduce((acc: number, item: any) => {
      if (typeof item.product === 'object' && item.product) {
        const price = getItemPrice(item)
        return acc + price * (item.quantity || 1)
      }
      return acc
    }, 0)

    return calculatedSubtotal
  }, [cart])

  // Calculate manual coupon discount and breakdown dynamically based on cart and coupon details
  const manualCouponData = useMemo(() => {
    if (!couponDetails || !cart?.items)
      return { total: 0, breakdown: [] as DiscountBreakdownItem[] }

    let discountSum = 0
    const breakdown: DiscountBreakdownItem[] = []
    const d = couponDetails
    const items = cart.items as any[]

    if (d.appliesTo === 'product' && Array.isArray(d.products)) {
      // Apply to specific products
      const productIds = d.products.map((p: any) => (typeof p === 'object' ? p.id : p))

      items.forEach((item) => {
        const productId = typeof item.product === 'object' ? item.product.id : item.product
        if (productIds.includes(productId)) {
          const price = getItemPrice(item)
          const quantity = item.quantity || 1
          const itemSubtotal = price * quantity
          const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
          discountSum += discountAmount

          const product = item.product
          breakdown.push({
            discountName: d.name,
            discountMode: 'manual',
            productTitle: typeof product === 'object' ? product.title : `Product #${product}`,
            productId: typeof product === 'object' ? product.id : product,
            originalPrice: itemSubtotal,
            discountAmount,
            discountPercentage: d.discountPercentage,
            quantity,
          })
        }
      })
    } else if (d.appliesTo === 'category' && Array.isArray(d.category)) {
      // Apply to specific categories
      const categoryIds = d.category.map((c: any) => (typeof c === 'object' ? c.id : c))

      // Track processed item IDs to avoid double counting if item matches multiple criteria (though unlikely here)
      // logic simplified: iterate items and check category
      items.forEach((item) => {
        if (item.product && typeof item.product === 'object') {
          const hasCategory = item.product.categories?.some((c: any) => {
            const catId = typeof c === 'object' ? c.id : c
            return categoryIds.includes(catId)
          })

          if (hasCategory) {
            const price = getItemPrice(item)
            const quantity = item.quantity || 1
            const itemSubtotal = price * quantity
            const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
            discountSum += discountAmount

            breakdown.push({
              discountName: d.name,
              discountMode: 'manual',
              productTitle: item.product.title,
              productId: item.product.id,
              originalPrice: itemSubtotal,
              discountAmount,
              discountPercentage: d.discountPercentage,
              quantity,
            })
          }
        }
      })
    } else {
      // Apply to all (default) or 'all' matches
      // Also handling 'coupon' mode default if appliesTo is missing (fallback to all)
      items.forEach((item) => {
        if (item.product) {
          const price = getItemPrice(item)
          const quantity = item.quantity || 1
          const itemSubtotal = price * quantity
          const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
          discountSum += discountAmount

          const product = item.product
          breakdown.push({
            discountName: d.name,
            discountMode: 'manual',
            productTitle: typeof product === 'object' ? product.title : `Product #${product}`,
            productId: typeof product === 'object' ? product.id : product,
            originalPrice: itemSubtotal,
            discountAmount,
            discountPercentage: d.discountPercentage,
            quantity,
          })
        }
      })
    }

    return { total: discountSum, breakdown }
  }, [cart, couponDetails])

  const manualCouponDiscount = manualCouponData.total
  const allDiscountBreakdown = useMemo(() => {
    return [...discountBreakdown, ...manualCouponData.breakdown]
  }, [discountBreakdown, manualCouponData.breakdown])

  // Add memoized cart weight
  const cartWeightKg = useMemo(() => {
    if (!cart?.items || !Array.isArray(cart.items)) return 0
    return (cart.items as any[]).reduce((total, item) => {
      const productWeight = item.product && typeof item.product === 'object' ? item.product.weightKg : null
      const variantWeight = item.variant && typeof item.variant === 'object' ? item.variant.weightKg : null
      const weight = resolveItemWeight(productWeight, variantWeight)
      return total + (weight ?? 0) * (item.quantity || 1)
    }, 0)
  }, [cart])

  // Calculate shipping info based on subtotal and country
  const shippingInfo = useMemo(() => {
    // Calculate shipping based on the original subtotal (pre-discount),
    // so discounts don't make you lose free shipping.
    return calculateShipping(cartSubtotal, shippingCountry, containsManualDeliveryItems, cartWeightKg || null)
  }, [cartSubtotal, shippingCountry, containsManualDeliveryItems, cartWeightKg])

  // Helper: Get total quantity of items in a specific category
  const getCategoryQuantity = useCallback(
    (categoryId: string | number): number => {
      if (!cart?.items) return 0
      return (cart.items as any[]).reduce((acc: number, item: any) => {
        if (
          item.product &&
          typeof item.product === 'object' &&
          item.product.categories?.some(
            (c: any) => (typeof c === 'object' ? c.id : c) === categoryId,
          )
        ) {
          return acc + (item.quantity || 0)
        }
        return acc
      }, 0)
    },
    [cart],
  )

  // Helper: Get subtotal of items in a specific category
  // (Only used within fetchActiveDiscounts but kept for potential future use)
  const getCategorySubtotal = useCallback(
    (categoryId: string | number): number => {
      if (!cart?.items) return 0
      return (cart.items as any[]).reduce((acc: number, item: any) => {
        if (
          item.product &&
          typeof item.product === 'object' &&
          item.product.categories?.some(
            (c: any) => (typeof c === 'object' ? c.id : c) === categoryId,
          )
        ) {
          const price = getItemPrice(item)
          return acc + price * (item.quantity || 0)
        }
        return acc
      }, 0)
    },
    [cart],
  )

  // Fetch active discounts when cart changes
  useEffect(() => {
    const fetchActiveDiscounts = async () => {
      if (!cart?.items || cart.items.length === 0) {
        setBulkVolumeDiscount(0)
        setDiscountBreakdown([])
        return
      }

      try {
        const res = await fetch('/api/discounts/active')
        if (!res.ok) {
          // API might not exist yet - this is fine
          console.debug('Active discounts API not available')
          setBulkVolumeDiscount(0)
          setDiscountBreakdown([])
          return
        }

        const { discounts } = await res.json()
        if (!discounts || !Array.isArray(discounts)) {
          setBulkVolumeDiscount(0)
          setDiscountBreakdown([])
          return
        }

        let autoDiscountSum = 0
        const breakdown: DiscountBreakdownItem[] = []

        // Calculate discounts based on mode
        discounts.forEach((d: any) => {
          if (d.discountMode === 'automatic') {
            // Automatic: Percentage off the entire cart
            const discountAmount = Math.round(cartSubtotal * (d.discountPercentage / 100))
            autoDiscountSum += discountAmount
            breakdown.push({
              discountName: d.name,
              discountMode: 'automatic',
              productTitle: 'Entire Cart',
              productId: 0,
              originalPrice: cartSubtotal,
              discountAmount,
              discountPercentage: d.discountPercentage,
              quantity: 1,
            })
          } else if (d.discountMode === 'manual') {
            // ... existing manual logic ...
            // Logic kept as is in original for manual *active* discounts (which are different from coupon)
            // Track processed item IDs to prevent duplicate discounts for items in multiple matching categories
            const processedItemIds = new Set<number | string>()

            // Manual: Pre-applied discount on specific products
            if (d.appliesTo === 'product' && Array.isArray(d.products)) {
              // Sum discount for each matching product in cart
              const productIds = d.products
              const matchingItems = (cart.items as any[]).filter((item: any) => {
                const productId = typeof item.product === 'object' ? item.product.id : item.product
                return productIds.includes(productId)
              })

              matchingItems.forEach((item: any) => {
                const price = getItemPrice(item)
                const quantity = item.quantity || 1
                const itemSubtotal = price * quantity
                const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
                autoDiscountSum += discountAmount

                const product = item.product
                breakdown.push({
                  discountName: d.name,
                  discountMode: 'manual',
                  productTitle: typeof product === 'object' ? product.title : `Product #${product}`,
                  productId: typeof product === 'object' ? product.id : product,
                  originalPrice: itemSubtotal,
                  discountAmount,
                  discountPercentage: d.discountPercentage,
                  quantity,
                })
              })
            } else if (d.appliesTo === 'category' && Array.isArray(d.category)) {
              // Manual discount on category - collect all matching items first, then dedupe
              const matchingItems = (cart.items as any[]).filter((item: any) => {
                if (!item.product || typeof item.product !== 'object') return false
                // Check if item belongs to ANY of the discount's categories
                return item.product.categories?.some((c: any) => {
                  const catId = typeof c === 'object' ? c.id : c
                  return d.category.includes(catId)
                })
              })

              matchingItems.forEach((item: any) => {
                const product = item.product
                const productId = typeof product === 'object' ? product.id : product

                // Skip if already processed for this discount
                if (processedItemIds.has(productId)) return
                processedItemIds.add(productId)

                const price = getItemPrice(item)
                const quantity = item.quantity || 1
                const itemSubtotal = price * quantity
                const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
                autoDiscountSum += discountAmount

                breakdown.push({
                  discountName: d.name,
                  discountMode: 'manual',
                  productTitle: typeof product === 'object' ? product.title : `Product #${product}`,
                  productId,
                  originalPrice: itemSubtotal,
                  discountAmount,
                  discountPercentage: d.discountPercentage,
                  quantity,
                })
              })
            } else if (d.appliesTo === 'all') {
              // Manual discount on all products - track per product
              ;(cart.items as any[]).forEach((item: any) => {
                if (item.product && typeof item.product === 'object') {
                  const price = getItemPrice(item)
                  const quantity = item.quantity || 1
                  const itemSubtotal = price * quantity
                  const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
                  autoDiscountSum += discountAmount

                  breakdown.push({
                    discountName: d.name,
                    discountMode: 'manual',
                    productTitle: item.product.title,
                    productId: item.product.id,
                    originalPrice: itemSubtotal,
                    discountAmount,
                    discountPercentage: d.discountPercentage,
                    quantity,
                  })
                }
              })
            }
          } else if (d.discountMode === 'bulk') {
            // ... bulk logic kept as is ...
            // Track processed item IDs to prevent duplicate discounts
            const bulkProcessedItemIds = new Set<number | string>()

            // Bulk volume discount for specific category
            if (d.applyAsVolumePricing && Array.isArray(d.category)) {
              // First, calculate total quantity across ALL discount categories
              const totalCategoryQuantity = d.category.reduce((sum: number, catId: number) => {
                return sum + getCategoryQuantity(catId)
              }, 0)

              const matchedTier = d.volumePricing
                ?.sort((a: any, b: any) => b.minQuantity - a.minQuantity)
                .find((t: any) => totalCategoryQuantity >= t.minQuantity)

              if (matchedTier) {
                // Collect all matching items from ANY of the discount's categories
                const matchingItems = (cart.items as any[]).filter((item: any) => {
                  if (!item.product || typeof item.product !== 'object') return false
                  return item.product.categories?.some((c: any) => {
                    const catId = typeof c === 'object' ? c.id : c
                    return d.category.includes(catId)
                  })
                })

                matchingItems.forEach((item: any) => {
                  const product = item.product
                  const productId = typeof product === 'object' ? product.id : product

                  // Skip if already processed for this discount
                  if (bulkProcessedItemIds.has(productId)) return
                  bulkProcessedItemIds.add(productId)

                  const price = getItemPrice(item)
                  const itemQty = item.quantity || 1
                  const itemSubtotal = price * itemQty
                  const discountAmount = Math.round(
                    itemSubtotal * (matchedTier.discountPercentage / 100),
                  )
                  autoDiscountSum += discountAmount

                  breakdown.push({
                    discountName: `${d.name} (${totalCategoryQuantity}+ items)`,
                    discountMode: 'bulk',
                    productTitle:
                      typeof product === 'object' ? product.title : `Product #${product}`,
                    productId,
                    originalPrice: itemSubtotal,
                    discountAmount,
                    discountPercentage: matchedTier.discountPercentage,
                    quantity: itemQty,
                  })
                })
              }
            } else if (d.bulkQuantity) {
              // Simple bulk discount (fixed quantity threshold)
              if (d.appliesTo === 'category' && Array.isArray(d.category)) {
                d.category.forEach((catId: number) => {
                  const quantity = getCategoryQuantity(catId)
                  if (quantity >= d.bulkQuantity) {
                    const categoryItems = (cart.items as any[]).filter((item: any) => {
                      return (
                        item.product &&
                        typeof item.product === 'object' &&
                        item.product.categories?.some(
                          (c: any) => (typeof c === 'object' ? c.id : c) === catId,
                        )
                      )
                    })

                    categoryItems.forEach((item: any) => {
                      const price = getItemPrice(item)
                      const itemQty = item.quantity || 1
                      const itemSubtotal = price * itemQty
                      const discountAmount = Math.round(itemSubtotal * (d.discountPercentage / 100))
                      autoDiscountSum += discountAmount

                      const product = item.product
                      breakdown.push({
                        discountName: d.name,
                        discountMode: 'bulk',
                        productTitle:
                          typeof product === 'object' ? product.title : `Product #${product}`,
                        productId: typeof product === 'object' ? product.id : product,
                        originalPrice: itemSubtotal,
                        discountAmount,
                        discountPercentage: d.discountPercentage,
                        quantity: itemQty,
                      })
                    })
                  }
                })
              }
            }
          }
        })

        setBulkVolumeDiscount(autoDiscountSum)
        setDiscountBreakdown(breakdown)
      } catch (error) {
        console.error('Error fetching active discounts:', error)
        setBulkVolumeDiscount(0)
        setDiscountBreakdown([])
      }
    }

    fetchActiveDiscounts()
  }, [cart, cartSubtotal, getCategoryQuantity])

  // Calculate final cart total (subtotal - discounts + shipping)
  const cartTotal = useMemo(() => {
    const shippingCost = shippingInfo?.shippingCost || 0
    const raw = Math.max(0, cartSubtotal - manualCouponDiscount - bulkVolumeDiscount + shippingCost)

    return {
      formatted: (raw / 100).toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      raw,
    }
  }, [cartSubtotal, manualCouponDiscount, bulkVolumeDiscount, shippingInfo])

  // Apply a promo code
  const applyCoupon = useCallback(
    async (promoCode: string): Promise<CouponResponse> => {
      if (!user?.id) {
        return { success: false, message: 'User is not logged in.' }
      }

      try {
        // Prepare cart items for validation
        const cartItems =
          cart?.items?.map((item: any) => ({
            product: item.product,
            quantity: item.quantity,
            variant: item.variant,
          })) || []

        const response = await fetch('/api/discounts/apply', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code: promoCode,
            userId: user.id,
            cartItems, // Send cart items for validation
          }),
        })

        if (!response.ok) {
          const errorData = await response.json()
          return { success: false, message: errorData.error || 'Invalid coupon code' }
        }

        const data = await response.json()
        setCouponDetails(data)
        setCouponId(data.discountId)

        return { success: true }
      } catch (error: any) {
        console.error('Error applying coupon:', error)
        return { success: false, message: error?.message || 'Failed to apply coupon' }
      }
    },
    [user?.id, cart?.items],
  )

  // Remove applied coupon
  const removeCoupon = useCallback(() => {
    setCouponDetails(null)
    setCouponId(null)
  }, [])

  // Determine if cart is empty
  const cartIsEmpty = useMemo(() => {
    return hasInitializedCart && (!cart?.items || cart.items.length === 0)
  }, [hasInitializedCart, cart])

  const value: EnhancedCartContextType = {
    // Pass through plugin cart functions
    cart,
    addItem,
    removeItem,
    clearCart: () => {
      clearCart()
      removeCoupon()
      setShippingCountry('AT')
    },
    incrementItem,
    decrementItem,
    isLoading,
    // Enhanced features
    cartSubtotal,
    cartTotal,
    shippingInfo,
    shippingCountry,
    setShippingCountry,
    bulkVolumeDiscount,
    discountBreakdown: allDiscountBreakdown,
    manualCouponDiscount,
    couponId,
    applyCoupon,
    removeCoupon,
    hasInitializedCart,
    cartIsEmpty,
    containsManualDeliveryItems,
  }

  return <EnhancedCartContext.Provider value={value}>{children}</EnhancedCartContext.Provider>
}
